import React, { useEffect, useRef, useState } from 'react'
import PostalMime, { Email } from 'postal-mime';
import * as XLSX from 'xlsx';
import Spreadsheet from 'react-spreadsheet'
import MsgReader, { FieldsData } from '@kenjiuno/msgreader'
import * as DocxPreview from 'docx-preview';
import { pdfjs, Document, Page } from 'react-pdf';
import { mergeRefs } from '@mantine/hooks';
import { PanelRightClose, PanelRightOpen, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '../ui/button';
import { downloadFile } from 'src/functions/actions/documents';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {Buffer} from 'buffer';
import toast from 'react-hot-toast';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;


const validExtensions = [
  'eml', 'xlsx', 'docx', 'pdf', 'bpm', 'jpg', 'png', 'jpeg', 'pptx', 'txt', 'doc', 'msg', 'mp4', 'jfif'
];

export type Extension = 'eml' | 'xlsx' | 'docx' |  'pdf' | 'bpm' | 'jpg' | 'png' | 'jpeg' | 'pptx' | 'txt' | 'doc' | 'msg' | 'mp4' | 'jfif'

export interface Excel {
  name: string;
  data: any[]
}

export interface Msg { msg:FieldsData }

export interface Ppt { ppt:string | null }

export interface Txt { txt:string }

export interface Dflt { dft:string }

type Options = {
  pdf?: {
    zoom?: number,
    showSidebar?: boolean
    showZoombar?: boolean
  }
}

type FilePreviewProps = {
    base64?: string;
    extension?: Extension
    classNameContainer?: string;
    options?: Options
}

const FilePreview = ({base64, extension, classNameContainer, options}:FilePreviewProps) => {
    const [content, setContent] = useState<Email | Excel[] | Ppt | Msg | Txt | Dflt | null>(null);
    const [selected_index, setselected_index] = useState(0);
    const previewRef = useRef<HTMLDivElement>()

    useEffect(()=>{
      setContent(null);

      if(extension && !validExtensions.includes(extension)){
        setContent({dft:base64||''})
        return;
      }

      switch (extension) {
        case 'eml':
          getEmail();
          break;
        case 'xlsx':
          getExcel();
          break;
        case 'docx':
        case 'doc':
          getDocx()
          break;
        case 'bpm':
          getBpm()
          break;
        case 'pptx':
          getPpt()
          break;
        case 'txt':
          getTxt()
          break;
        case 'msg':
          getMsg();
          break;
        default:
          break;
      }

      console.log(extension);
      
    }, [base64])    

    const getEmail = async () => {
      try {
        const rawEmlContent = atob(base64 || '');
        const parser = new PostalMime();
        const email = await parser.parse(rawEmlContent)
        setContent(email)
      } catch (error) {
        console.error('Error getting email content:', error);
      }
    };

    const getExcel = async () => {
      try {
        const rawExcelContent = atob(base64 || '');
        const workbook = XLSX.read(rawExcelContent, { type: 'binary' });
        const sheetsData:any[] = [];

        const alphabetLength = 26; // Number of columns from A to Z

        workbook.SheetNames.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          let data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Ensure each row has at least 26 columns (A to Z)
          data = data.map((row:any) => {
            while (row.length < alphabetLength) {
              row.push(''); // Add empty cells if the row has less than 26 columns
            }
            return row.map((cell:any) => ({ value: cell || '' }));
          });

          // Ensure the sheet has at least 1 row with 26 columns if it's empty
          if (data.length === 0) {
            data.push(new Array(alphabetLength).fill({ value: '' }));
          }

          sheetsData.push({ name: sheetName, data });
        });

        setContent(sheetsData);
      } catch (error) {
        console.error('Error getting excel content:', error);
      }
    }

    const getDocx = async () => {
      const rawDocxContent = Buffer.from(base64 || '', 'base64');
      if(previewRef.current){
        DocxPreview
          .renderAsync(rawDocxContent, previewRef.current)
          .catch(err => {
            toast.error('Kon de docx niet renderen. Probeer het document te downloaden.', { duration: 5000 })
            console.log(err)
          })
   
        
      } else { console.warn('Cant render docx, no ref') }
      
    }

    const getBpm = async () => {
      // No solution found
      //https://view.officeapps.live.com/op/view.aspx?src=http://sspeedtesting.herokuapp.com/officeviewer/upload/temp/office/qastan-code-project-expectations.pptx
    }

    const getPpt = async () => {      
      try {
        

        setContent({ppt:base64 ? base64 : ''})
        // console.log(base64)
        // let { data } = await axios.post(`https://fitdoc.herokuapp.com/upload`, {
        //   file: base64,
        //   filename: 'Preview.pptx'
        // })
        // if(data.errorcode!==0){ throw new Error(data.message) }
        // setContent({ppt:data.file})
      } catch (error) {
        console.log(error)
        setContent({ppt:null})
      }
    }

    const getTxt = async () => {
      const byteCharacters = atob(base64 || '');
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/plain' });

      // Create Blob URI
      const blobURI = URL.createObjectURL(blob);
      setContent({txt:blobURI})
    }

    const getMsg = () => {
      try {
        const byteCharacters = atob(base64 || '');
        // Step 2: Convert byte array to ArrayBuffer
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const arrayBuffer = byteArray.buffer;

        const testMsg = new MsgReader(arrayBuffer);
        const msgInfo = testMsg.getFileData();

        setContent({msg:msgInfo})
      } catch (e){
        console.log('Error reading msg: ', e)
      }
    }

    return (
    <>
      {(content && ('dft' in content) && content.dft) && <div className='flex flex-col items-center justify-center py-6'><p className='font-semibold text-center'>Geen beschikbare preview gevonden</p><p className='max-w-sm text-sm text-center text-neutral-400'>Download het bestand om het document te openen op uw computer</p><Button onClick={()=>{downloadFile(content.dft as string, `document.${extension}`)}} className='mt-6' variant='outline'>Download document</Button></div>}
      {(extension === 'eml' && content && 'html' in content && content.html) && <div dangerouslySetInnerHTML={{__html:content.html}}></div>}
      {(extension === 'xlsx' && content && Array.isArray(content) && content[selected_index] && 'data' in content[selected_index]) && <XLSXPreview content={content}/>}
      {(extension === 'docx' || extension === 'doc') && <div ref={mergeRefs(previewRef)}></div>}
      {(extension === 'pdf' && base64) && <PDFPreview options={options} base64={base64}/>}
      {(extension === 'bpm' && base64) && <div className='flex flex-col items-center'><img className='opacity-75 w-[250px] h-auto' src={""} alt="No preview"/><p className='text-lg font-semibold'>Geen BPM-preview beschikbaar</p><p>Probeer uw diagram te exporteren als afbeelding</p><p className='text-sm text-neutral-400'>Beschikbare formaten: PNG, SVG, JPG</p></div>}
      {((extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'jfif') && base64) && <IMGPreview base64={base64}/>}
      {((extension === 'mp4') && base64) && <video autoPlay controls src={`data:video/mp4;base64,${base64}`}>The video-tag is not supported by your browser.</video>}
      {/* {(extension === 'pptx' && content && 'ppt' in content) && <>{content.ppt ?<div className='w-full h-full bg-neutral-600'>  <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=https://cdn.qastan.be/Fitdoc/preview/${content.ppt}&embedded=true`} className='w-full h-full'/></div>:<div className='flex flex-col items-center'><img className='opacity-75 w-[250px] h-auto' src={""} alt="No preview"/><p className='text-lg font-semibold'>Geen PPT-preview beschikbaar</p><p>Probeer later opnieuw</p><p className='text-sm text-neutral-400'>U kan altijd uw Powerpoint exporteren als PDF</p></div>}</>} */}
      {(extension === 'pptx' && content && 'ppt' in content && content.ppt) && <PPTXPrev base64={content.ppt}/>}

      {(extension === 'txt' && content && 'txt' in content) && <iframe src={content.txt} className='w-full h-full'/>}
      {(extension === 'msg' && content && 'msg' in content) && <div><div className='py-4 mb-4 border-b'><p className='text-lg font-medium'>{content.msg.subject}</p><div className='flex items-center gap-2 my-2'><div className='flex items-center justify-center w-10 h-10 rounded-full select-none min-w-10 min-h-10 bg-neutral-100'><p className='text-lg font-bold opacity-30'>{content.msg.senderName?.charAt(0)}</p></div><div><p className='font-medium'>{content.msg.senderEmail}</p><p className='text-sm text-neutral-600'>{content.msg.senderName}</p></div></div><div className='flex items-center gap-1'><p className='text-sm text-neutral-600'>Aan:</p>{content.msg.recipients?.map((recipient, index)=>(<p key={index} className='text-sm font-medium'>{recipient.smtpAddress} {recipient.name}</p>))}</div></div><p className='whitespace-pre'>{content.msg.body}</p></div>}
    </>
    )
}

const IMGPreview = ({base64}:{base64:string}) => {
  return (
    <div className='flex justify-center border'>
    <TransformWrapper minScale={0.5}>
      <TransformComponent>
      <img src={`data:image/png;base64,${base64}`} className='h-full max-h-[calc(100dvh-100px-16px-32px)]'/>
      </TransformComponent>
    </TransformWrapper>
    </div>
  )
}

export const PDFPreview = ({base64, options}:{base64:string, options?:Options}) => {
  const showZoombar = typeof(options?.pdf?.showZoombar) === 'boolean' ? options.pdf.showZoombar : true;
  
  const [open_sidebar, setopen_sidebar] = useState<boolean>(typeof(options?.pdf?.showSidebar) === 'boolean' ? options.pdf.showSidebar : true);
  const [active_page, setactive_page] = useState<string | number>(1);
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(options?.pdf?.zoom ? options.pdf.zoom : 1.0);
  const observer = useRef<IntersectionObserver | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null)

  const [pagesRendered, setPagesRendered] = useState<number>(0);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const pageNumber = Number(entry.target.getAttribute('data-page-number'));
            setactive_page(pageNumber);
          }
        });
      },
      { threshold: [0, 1.0] }
    );

    const currentObserver = observer.current;

    // Observe all page elements
    Object.values(pageRefs.current).forEach((page) => {
      if (page) {
        currentObserver.observe(page);
      }
    });

    // Cleanup on unmount
    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
      }
    };
  }, [numPages]);

  function goToPage(pageNumber:number) {
    pageRefs.current[pageNumber]?.scrollIntoView({ behavior: 'smooth' });
  }

  const clamp = (val: number) => {
    const minValue = 1 !== undefined ? Number(1) : undefined;
    const maxValue = numPages !== undefined ? Number(numPages) : undefined;
    
    if (minValue !== undefined && val < minValue) return minValue;
    if (maxValue !== undefined && val > maxValue) return maxValue;
    return val;
  };

  const handleIncrease = () => {
      setactive_page(prev => {
          const numericValue = typeof prev === 'number' ? prev : parseFloat(prev);
          const newValue = isNaN(numericValue) ? 1 : numericValue + 1;
          goToPage(clamp(newValue))
          return clamp(newValue);
      });
  };

  const handleDecrease = () => {
      setactive_page(prev => {
          const numericValue = typeof prev === 'number' ? prev : parseFloat(prev);
          const newValue = isNaN(numericValue) ? -1 : numericValue - 1;
          goToPage(clamp(newValue))
          return clamp(newValue);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (/^-?\d*\.?\d*$/.test(newValue)) {
        goToPage(parseFloat(newValue))
        setactive_page(newValue);
      }
  };

  const handleBlur = () => {
      const numericValue = parseFloat(active_page as string);
      if (isNaN(numericValue)) {
          setactive_page(clamp(0));
      } else {
          setactive_page(clamp(numericValue));
      }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowUp') {
          handleIncrease();
          e.preventDefault();
      } else if (e.key === 'ArrowDown') {
          handleDecrease();
          e.preventDefault();
      }
  };

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setPagesRendered(0); // Reset the pages rendered counter on load success
  }

  const onRenderSuccess = () => {
    setPagesRendered((prev) => Math.min(prev + 1, numPages));
  };

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 3.0));
  };

  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
  };

  const [scrollHeight, setscrollHeight] = useState<number>(window.innerHeight);
  useEffect(()=>{
    if(contentRef.current){
      const scrollHeight = contentRef.current.parentElement?.parentElement?.parentElement?.clientHeight || window.innerHeight
      setscrollHeight(scrollHeight)
    }
  }, [])

  return (
    <>
    {showZoombar &&
    <div className='sticky top-0 z-30 flex items-center h-12 gap-1 p-1 text-white -translate-x-1/2 rounded-full w-fit left-1/2 bg-black/60 backdrop-blur-sm'>
        <button onClick={()=>{setopen_sidebar(!open_sidebar)}} className='flex items-center justify-center w-10 h-10 text-white rounded-full min-w-10 hover:bg-white/10'>
          {open_sidebar ? <PanelRightClose className='w-4 h-4 rotate-180'/> : <PanelRightOpen className='w-4 h-4 rotate-180'/>}
        </button>
        <div className='flex items-center'>
          <input 
            value={active_page} 
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown} 
            className='w-10 h-10 px-2 text-center text-white bg-transparent rounded-full focus:outline-none hover:bg-white/10'
          />
          <p className='w-10 text-sm select-none text-white/50'>/<span className='ml-4'>{numPages}</span></p>
        </div>
        <button onClick={()=>{handleZoomIn()}} className='flex items-center justify-center w-10 h-10 text-white rounded-full min-w-10 hover:bg-white/10'><ZoomIn className='w-4 h-4'/></button>
        <p className='text-sm text-white opacity-50 select-none'>{Math.round(scale*100)}%</p>
        <button onClick={()=>{handleZoomOut()}} className='flex items-center justify-center w-10 h-10 text-white rounded-full min-w-10 hover:bg-white/10'><ZoomOut className='w-4 h-4'/></button>
    </div>}

    <div ref={contentRef} className='flex w-full gap-4 bg-neutral-50'>
      <div style={{maxHeight:scrollHeight+'px'}} className={`${open_sidebar ? 'px-2 overflow-auto max-w-[auto] min-w-36' : 'px-0 overflow-hidden max-w-0'} sticky top-0 h-full duration-200 py-2 flex flex-col items-center gap-2 bg-neutral-100 rounded-sm`}>
        <div>
          <Document file={`data:application/pdf;base64,${base64}`} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(
              new Array(Math.min(pagesRendered + 1, numPages)),
              (el, index) => (
                <div onClick={() => { goToPage(index + 1) }} key={index} className="cursor-pointer !select-none last:mb-0 w-fit border min-w-32 bg-white">
                  <Page pageNumber={index + 1} scale={0.2} onRenderSuccess={onRenderSuccess} />
                </div>
              )
            )}
            {/* {Array.apply(null, Array(numPages)).map((x, i)=>i+1).map(page => (
              <div onClick={()=>{goToPage(page)}} key={page} className='cursor-pointer !select-none last:mb-0 w-fit border min-w-32 bg-white'>
                <Page pageNumber={page} scale={0.2}/>
              </div>
            ))} */}
          </Document>
        </div>
      </div>
      
      <div className='p-2'>
        <Document file={`data:application/pdf;base64,${base64}`} onLoadSuccess={onDocumentLoadSuccess} >
          {Array.from(
            new Array(Math.min(pagesRendered + 1, numPages)),
            (el, index) => (
              <div key={index} className="mb-4 border shadow-sm last:mb-0 w-fit" ref={el => { pageRefs.current[index + 1] = el; }} data-page-number={index + 1}>
                <Page pageNumber={index + 1} scale={scale} onRenderSuccess={onRenderSuccess} />
              </div>
            )
          )}
          {/* {Array.apply(null, Array(numPages)).map((x, i)=>i+1).map(page => (
            <div key={page} className='mb-4 border shadow-sm last:mb-0 w-fit' ref={el => { pageRefs.current[page] = el; }} data-page-number={page}>
              <Page pageNumber={page} scale={scale} />
            </div>
          ))} */}
        </Document>
      </div>
    </div>
    </>
  )
}

const XLSXPreview = ({content}:{content:Excel[]}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [selected_index, setselected_index] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const overlay = overlayRef.current;

    if (container && overlay) {
      const updateOverlaySize = () => {
        overlay.style.width = `${container.scrollWidth}px`;
        overlay.style.height = `${container.scrollHeight}px`;
      };

      // Initial update
      updateOverlaySize();

      // Update on window resize
      window.addEventListener('resize', updateOverlaySize);

      return () => {
        window.removeEventListener('resize', updateOverlaySize);
      };
    }
  }, [content, selected_index]);

  return (
    <div >
      <div className='flex items-center mb-3 overflow-auto border'>
        {content.map((page,index)=>(
          <button onClick={()=>{setselected_index(index)}} className={`${selected_index===index?'bg-neutral-100 text-primary':'bg-white text-neutral-400 font-normal'} text-sm font-medium px-2 py-2 border-r border-y-0 border-l-0 border-neutral-200 border-solid`}>
            {page.name}
          </button>
        ))}
      </div>
      <div className='relative overflow-auto' ref={containerRef}>
        <div ref={overlayRef} className='absolute top-0 left-0 z-10 bg-black/0'></div>
        <Spreadsheet className='text-sm' data={content[selected_index].data as any}/>
      </div>
    </div>
  )
}

const PPTXPrev = ({base64}:{base64:string}) => {
  const ref = useRef(null)

  function base64ToBlob(base64:string) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
  }
  
  useEffect(() => {
    if(!ref){ return; }

    const blob = base64ToBlob(base64)
    const blobUrl = URL.createObjectURL(blob);

    //@ts-ignore
    $("#slide-resolte-contaniner").pptxToHtml({
      pptxFileUrl: `${blobUrl}`,
      fileInputId: "", /*id of input tag*/ 
      slideMode: false,
      slidesScale: "80",
      keyBoardShortCut: false,
    })
  }, []);

  return (
    <div id="slide-resolte-contaniner" className='absolute h-full origin-left top-4 left-4'></div>
  )
}

export default FilePreview